import type { PropsWithChildren, ReactNode } from 'react'
import React, { Children, useEffect } from 'react'
import { useId } from '../hooks/useId'
import Menu from '../Menu/Menu'
import { ActionTypes } from './Actions'
import { DisplayModes, useAutocomplete } from './Autocomplete'

interface GroupProps {
  /**
   * Controls when the group should be displayed based on search state.
   * If set to `always`, the group is always shown, regardless of the search term. When set to `withTerm`, the group is shown only when there is a search term entered. When set to `withoutTerm`, the group is shown only when there is no search term
   * @default DisplayModes.WithTerm
   */
  display?: DisplayModes
  /**
   * The label for the group
   */
  label?: ReactNode
}

function Group({
  children,
  display = DisplayModes.WithTerm,
  label = null,
}: PropsWithChildren<GroupProps>): JSX.Element | null {
  const [{ searching }, dispatch] = useAutocomplete()
  const id = useId()
  const show =
    display === DisplayModes.Always ||
    (searching
      ? display === DisplayModes.WithTerm
      : display === DisplayModes.WithoutTerm)

  useEffect(() => {
    dispatch({
      type: ActionTypes.RegisterGroup,
      id: id,
      data: { display, hasChildren: Children.count(children) > 0 },
    })
  }, [children, dispatch, display, id])

  useEffect(() => {
    return () => {
      dispatch({
        type: ActionTypes.UnregisterGroup,
        id: id,
      })
    }
  }, [dispatch, id])

  return show && Children.count(children) > 0 ? (
    <Menu.Group label={label}>{children}</Menu.Group>
  ) : null
}

Group.uiName = 'Autocomplete.Group'

export default Group
